import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CepCobertura {
  cep: string;
  tipo_servico: string | null;
  velocidade_max_mbps: number | null;
  ativo: boolean;
  ultima_atualizacao: string;
  fonte: string | null;
}

/**
 * Busca cobertura para múltiplos CEPs de uma vez (SELECT IN).
 *
 * Usado pelo DossieCnpjCard para checar a cobertura de TODOS os endereços
 * encontrados (matriz da Receita, endereços da Assertiva CNPJ, endereços
 * dos sócios da Assertiva CPF) sem depender do orquestrador retornar
 * `cobertura_por_cep`. A lógica é puramente "se tem CEP, consulta CEP" —
 * a tabela não tem coluna CNPJ, é só CEP → ativo.
 *
 * Retorna um Map cep → { ativo, tipo_servico, velocidade_max_mbps }.
 * CEPs não encontrados na tabela aparecem no Map com ativo=false.
 */
export function useCoberturaPorCeps(ceps: string[]) {
  // Normaliza e dedup
  const cepsClean = Array.from(
    new Set((ceps || []).map((c) => String(c || '').replace(/\D/g, '')).filter((c) => c.length === 8))
  ).sort();

  return useQuery({
    queryKey: ['cobertura-multi', cepsClean.join(',')],
    enabled: cepsClean.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, { ativo: boolean; tipo_servico: string | null; velocidade_max_mbps: number | null }>> => {
      const result: Record<string, any> = {};
      // Inicializa todos como sem cobertura — a query só sobrescreve os que
      // existirem na tabela
      for (const c of cepsClean) {
        result[c] = { ativo: false, tipo_servico: null, velocidade_max_mbps: null };
      }
      const { data, error } = await supabase
        .from('cep_cobertura_claro_bl' as any)
        .select('cep, ativo, tipo_servico, velocidade_max_mbps')
        .in('cep', cepsClean);
      if (error) {
        console.warn('[Cobertura] erro ao consultar CEPs:', error);
        return result;
      }
      for (const row of (data as any[]) || []) {
        result[row.cep] = {
          ativo: !!row.ativo,
          tipo_servico: row.tipo_servico,
          velocidade_max_mbps: row.velocidade_max_mbps,
        };
      }
      return result;
    },
  });
}

/** Busca individual por CEP. */
export function useCepCobertura(cep: string | null | undefined) {
  const cepClean = (cep || '').replace(/\D/g, '');
  return useQuery({
    queryKey: ['cep-cobertura', cepClean],
    enabled: cepClean.length === 8,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CepCobertura | null> => {
      const { data, error } = await supabase
        .from('cep_cobertura_claro_bl' as any)
        .select('*')
        .eq('cep', cepClean)
        .maybeSingle();
      if (error) throw error;
      return (data as CepCobertura) || null;
    },
  });
}

/** Listagem paginada. */
export function useCepCoberturaList(filters: {
  search?: string;
  tipo?: string;
  pageSize?: number;
  page?: number;
} = {}) {
  const { search, tipo, pageSize = 50, page = 0 } = filters;

  return useQuery({
    queryKey: ['cep-cobertura-list', filters],
    staleTime: 30_000,
    queryFn: async () => {
      let q = supabase
        .from('cep_cobertura_claro_bl' as any)
        .select('*', { count: 'exact' })
        .order('ultima_atualizacao', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (search) {
        const s = search.replace(/\D/g, '');
        if (s.length >= 4) q = q.ilike('cep', `${s}%`);
      }
      if (tipo && tipo !== 'TODOS') q = q.eq('tipo_servico', tipo);

      const { data, error, count } = await q;
      if (error) throw error;
      return {
        rows: (data as unknown as CepCobertura[]) || [],
        total: count || 0,
        page,
        pageSize,
      };
    },
  });
}

// ── Helpers de parsing client-side ──────────────────────────────────────────

/** Normaliza qualquer entrada em CEP de 8 dígitos. Pública para uso em paste. */
export function normalizeCepInput(raw: any): string | null {
  const s = String(raw ?? '').replace(/\D/g, '');
  if (s.length === 7) return '0' + s; // CEPs que perderam leading zero
  if (s.length === 8) return s;
  return null;
}
const normalizeCep = normalizeCepInput;

function detectTipoServico(raw: any): string | null {
  if (!raw) return null;
  const s = String(raw).toUpperCase().trim();
  if (s.includes('FIBRA') || s === 'FTTH' || s === 'FTTX') return 'FIBRA';
  if (s.includes('HFC') || s.includes('CABO')) return 'HFC';
  if (s.includes('RADIO') || s.includes('RÁDIO')) return 'RADIO';
  if (s.includes('4G') || s.includes('FIXO_4G')) return '4G_FIXO';
  return s.slice(0, 32) || null;
}

interface CepRow {
  cep: string;
  tipo_servico: string | null;
  velocidade_max_mbps: number | null;
}

async function parseCepFile(file: File): Promise<CepRow[]> {
  const ab = await file.arrayBuffer();
  const ext = file.name.toLowerCase().split('.').pop() || '';

  // CSV simples: lê como texto e quebra por linha/vírgula/ponto-e-vírgula
  if (ext === 'csv' || ext === 'txt') {
    const text = new TextDecoder('utf-8').decode(ab);
    const rows = text
      .split(/\r?\n/)
      .map(l => l.split(/[;,\t]/).map(c => c.trim()))
      .filter(r => r.length > 0 && r[0]);
    const linhas: CepRow[] = [];
    // Detecta se a primeira linha é header
    const startIdx = normalizeCep(rows[0]?.[0]) ? 0 : 1;
    for (let i = startIdx; i < rows.length; i++) {
      const cep = normalizeCep(rows[i][0]);
      if (!cep) continue;
      linhas.push({
        cep,
        tipo_servico: detectTipoServico(rows[i][1]),
        velocidade_max_mbps:
          rows[i][2] != null && rows[i][2] !== '' && !isNaN(Number(rows[i][2]))
            ? Number(rows[i][2])
            : null,
      });
    }
    return linhas;
  }

  // XLSX/XLS
  const wb = XLSX.read(new Uint8Array(ab), { type: 'array', raw: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];
  const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as any[][];
  const linhas: CepRow[] = [];
  // Detecta se a primeira linha é header
  const startIdx = matrix.length > 0 && normalizeCep(matrix[0]?.[0]) ? 0 : 1;
  for (let i = startIdx; i < matrix.length; i++) {
    const row = matrix[i];
    if (!row || row.length === 0) continue;
    const cep = normalizeCep(row[0]);
    if (!cep) continue;
    linhas.push({
      cep,
      tipo_servico: detectTipoServico(row[1]),
      velocidade_max_mbps:
        typeof row[2] === 'number'
          ? row[2]
          : row[2] != null && row[2] !== '' && !isNaN(Number(row[2]))
            ? Number(row[2])
            : null,
    });
  }
  return linhas;
}

// ── Gravação no banco ──────────────────────────────────────────────────────
//
// Estratégia: INSERT DIRETO via supabase client é o caminho PRIMÁRIO
// (não depende de edge function, latência baixa, erros do Postgres claros).
// A edge function existe como recurso para volumes muito grandes onde o
// servidor pode bater limites do client — mas hoje o INSERT direto via
// supabase-js também batch-suporta volumes altos (chunks de 500) sem
// problemas para a maioria dos casos práticos.

interface GravacaoResult {
  inseridos: number;
  total_unicos: number;
  total_linhas_planilha: number;
  fonte: string;
  warnings: string[];
}

interface ProgressoCallback {
  (atual: number, total: number, etapa: string): void;
}

/**
 * Diagnóstico amigável de erro do Postgres/PostgREST.
 * Devolve a mensagem original se não reconhecer o padrão.
 */
function explicarErro(error: any): string {
  const msg = String(error?.message || error || '').toLowerCase();
  const code = String(error?.code || '');

  if (
    msg.includes('does not exist') ||
    msg.includes('relation') ||
    code === '42p01' ||
    code === 'pgrst205' ||
    msg.includes("could not find the table") ||
    msg.includes("not find the table 'public.cep_cobertura_claro_bl'")
  ) {
    return 'Tabela cep_cobertura_claro_bl ainda não foi criada. Faça novo deploy (`./scripts/deploy.sh`) — a migration 20260503140000_cep_cobertura_table_idempotente.sql cria a tabela. Se o erro persistir, abra o SQL Editor do Supabase e cole o conteúdo da migration manualmente.';
  }
  if (msg.includes('permission') || msg.includes('row-level') || code === '42501') {
    return 'Sem permissão para gravar em cep_cobertura_claro_bl. As policies RLS podem não estar criadas — re-aplique a migration 20260503100000.';
  }
  if (msg.includes('check constraint') || code === '23514') {
    return 'CEP fora do formato esperado (precisa ter 8 dígitos numéricos). Verifique a planilha.';
  }
  if (msg.includes('not authenticated') || code === '401') {
    return 'Sessão expirada. Faça login novamente.';
  }
  return error?.message || 'Erro desconhecido ao gravar CEPs no banco.';
}

/**
 * Caminho primário: INSERT direto via supabase client com upsert.
 * Faz batches de 500 (suficiente para evitar limites de payload sem
 * tornar o processo lento demais).
 */
async function gravarDireto(
  ceps: CepRow[],
  fonte: string,
  onProgress?: ProgressoCallback
): Promise<GravacaoResult> {
  const now = new Date().toISOString();
  const rows = ceps.map((c) => ({
    cep: c.cep,
    tipo_servico: c.tipo_servico,
    velocidade_max_mbps: c.velocidade_max_mbps,
    ativo: true,
    ultima_atualizacao: now,
    fonte,
  }));

  console.log('[Cobertura BL] Gravando direto via supabase client:', {
    total: rows.length,
    fonte,
  });

  let inseridos = 0;
  const BATCH = 500;
  const totalBatches = Math.ceil(rows.length / BATCH);
  for (let i = 0; i < rows.length; i += BATCH) {
    const slice = rows.slice(i, i + BATCH);
    const batchIdx = Math.floor(i / BATCH) + 1;
    onProgress?.(inseridos, rows.length, `Lote ${batchIdx}/${totalBatches}`);

    const { error } = await supabase
      .from('cep_cobertura_claro_bl' as any)
      .upsert(slice, { onConflict: 'cep' });

    if (error) {
      console.error('[Cobertura BL] Erro no batch', batchIdx, error);
      throw new Error(explicarErro(error));
    }
    inseridos += slice.length;
    console.log(`[Cobertura BL] Batch ${batchIdx}/${totalBatches} ok (${slice.length} CEPs)`);
  }

  onProgress?.(inseridos, rows.length, 'Finalizado');

  return {
    inseridos,
    total_unicos: rows.length,
    total_linhas_planilha: rows.length,
    fonte,
    warnings: [],
  };
}

/**
 * Importa CEPs Claro BL — aceita File (XLSX/CSV) ou array direto de CEPs
 * (vindo do paste). Caminho primário: INSERT direto via supabase client.
 *
 * Por que NÃO usar edge function como primário:
 * - Adiciona latência sem ganho de robustez.
 * - Quando a edge não está deployada, falha de formas inconsistentes
 *   (404, 500, network) que dificultam diagnóstico.
 * - INSERT direto via supabase-js já suporta batch grande (chunks de 500
 *   bypassam limite de payload) e respeita RLS naturalmente.
 *
 * Após gravar, faz uma SELECT count(*) explícita para mostrar ao usuário
 * o total de CEPs no banco AGORA, não só quantos foram inseridos no batch.
 */
export type CoberturaInput =
  | { tipo: 'arquivo'; file: File }
  | { tipo: 'paste'; ceps: Array<{ cep: string; tipo_servico?: string | null; velocidade_max_mbps?: number | null }>; fonte?: string };

export interface ImportProgresso {
  fase: 'parseando' | 'gravando' | 'finalizado' | null;
  atual: number;
  total: number;
  detalhe: string;
}

export function useImportarCobertura() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [progresso, setProgresso] = useState<ImportProgresso>({
    fase: null,
    atual: 0,
    total: 0,
    detalhe: '',
  });

  const mutation = useMutation({
    mutationFn: async (input: CoberturaInput | File): Promise<GravacaoResult & { totalNoBanco: number }> => {
      // Aceita File por compatibilidade com chamadas antigas
      const normalized: CoberturaInput =
        input instanceof File ? { tipo: 'arquivo', file: input } : input;

      // Fase 1: parsing
      setProgresso({ fase: 'parseando', atual: 0, total: 0, detalhe: 'Lendo entrada...' });
      let linhas: CepRow[] = [];
      let fonte = '';

      if (normalized.tipo === 'arquivo') {
        console.log('[Cobertura BL] Parseando arquivo:', normalized.file.name);
        linhas = await parseCepFile(normalized.file);
        fonte = normalized.file.name;
      } else {
        console.log('[Cobertura BL] Parseando paste com', normalized.ceps.length, 'entradas');
        fonte = normalized.fonte || `paste-${new Date().toISOString().slice(0, 16)}`;
        for (const c of normalized.ceps) {
          const cep = normalizeCep(c.cep);
          if (!cep) continue;
          linhas.push({
            cep,
            tipo_servico: detectTipoServico(c.tipo_servico),
            velocidade_max_mbps:
              typeof c.velocidade_max_mbps === 'number' ? c.velocidade_max_mbps : null,
          });
        }
      }

      console.log('[Cobertura BL] CEPs válidos parseados:', linhas.length);

      if (linhas.length === 0) {
        throw new Error('Nenhum CEP válido encontrado. Verifique o formato (8 dígitos por CEP).');
      }

      // Deduplicar
      const dedup = new Map<string, CepRow>();
      for (const l of linhas) dedup.set(l.cep, l);
      const linhasUnicas = Array.from(dedup.values());
      console.log('[Cobertura BL] CEPs únicos após dedup:', linhasUnicas.length);

      // Fase 2: gravação
      setProgresso({
        fase: 'gravando',
        atual: 0,
        total: linhasUnicas.length,
        detalhe: `Gravando ${linhasUnicas.length} CEPs...`,
      });

      const resultado = await gravarDireto(linhasUnicas, fonte, (atual, total, etapa) => {
        setProgresso({ fase: 'gravando', atual, total, detalhe: etapa });
      });

      // Fase 3: contar quantos CEPs há no banco AGORA
      setProgresso({
        fase: 'finalizado',
        atual: resultado.inseridos,
        total: linhasUnicas.length,
        detalhe: 'Validando no banco...',
      });

      const { count: totalNoBanco, error: countErr } = await supabase
        .from('cep_cobertura_claro_bl' as any)
        .select('cep', { count: 'exact', head: true });

      if (countErr) {
        console.warn('[Cobertura BL] Falha ao contar total no banco:', countErr);
      }

      console.log('[Cobertura BL] Total final no banco:', totalNoBanco);

      return {
        ...resultado,
        totalNoBanco: totalNoBanco || 0,
        total_linhas_planilha: linhas.length,
      };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['cep-cobertura-list'] });
      qc.invalidateQueries({ queryKey: ['cep-cobertura'] });
      toast({
        title: '✓ Cobertura importada',
        description: `${data.inseridos} CEPs gravados nesta importação. Total agora no banco: ${data.totalNoBanco} CEPs.`,
      });
      setProgresso({ fase: null, atual: 0, total: 0, detalhe: '' });
    },
    onError: (e: any) => {
      console.error('[Cobertura BL] Erro:', e);
      toast({
        title: 'Erro ao importar cobertura',
        description: e?.message || 'Erro desconhecido. Veja o console (F12) para detalhes técnicos.',
        variant: 'destructive',
      });
      setProgresso({ fase: null, atual: 0, total: 0, detalhe: '' });
    },
  });

  return {
    ...mutation,
    progresso,
  };
}
